/**
 * SitePick front — 商品詳細 FV のデバイス埋め込み（PC 枠 / SP 枠）。
 *
 * iframe は基準サイズ固定（PC 1280×800 / SP 390×845）で描画＝デモは PC / SP の実レイアウトで
 * レンダリングされる。site-devices.js が `--sp-shot-scale`（= 枠幅 / 基準幅）を設定し、
 * CSS の transform: scale で枠へぴったり縮小する（余白なし・スクロール不可の実物サムネ）。
 * ポスター（スクショ）は読み込み中のプレースホルダで、load 後にフェードアウト。
 * 高さ追従・postMessage は使わない（docs/07 §1 / §15）。
 */

import { promoteIframe } from "./iframe-frame.js";

/**
 * 1 つのデバイス枠を初期化する。
 *
 * @param {HTMLElement} shot `.c-device-shot`
 */
function initShot(shot) {
  const screen = shot.querySelector(".c-device-shot__screen");
  const iframe = shot.querySelector(".c-device-shot__iframe");
  const loading = shot.querySelector("[data-sp-shot-loading]");
  const fallback = shot.querySelector("[data-sp-shot-fallback]");
  const base = parseInt(shot.getAttribute("data-sp-base") || "0", 10);

  if (!screen || !(base > 0)) {
    return;
  }

  // --- 枠幅 → 基準幅の scale を確定的に設定（ResizeObserver で初期レイアウト後も追従） ---
  const applyScale = () => {
    const w = screen.clientWidth || screen.getBoundingClientRect().width;
    if (w > 0) {
      screen.style.setProperty("--sp-shot-scale", String(Math.min(1, w / base)));
    }
  };

  if ("ResizeObserver" in window) {
    new ResizeObserver(applyScale).observe(screen);
  } else {
    applyScale();
    let raf = 0;
    const onResize = () => {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(applyScale);
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
  }

  if (!(iframe instanceof HTMLIFrameElement) || !iframe.getAttribute("data-sp-src")) {
    return; // デモ無し（ポスターのみ）
  }

  // --- 起動（1 回だけ）。詳細ページ FV は「実物表示」が主目的なので、
  //     スクロール待ちにせず idle で読み込む（長時間ポスターのままにしない）。 ---
  let started = false;
  const start = () => {
    if (started) {
      return;
    }
    started = true;
    applyScale();
    promoteIframe(iframe, { loading, fallback, onLoad: applyScale });
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(start, { timeout: 1800 });
  } else {
    window.setTimeout(start, 500);
  }
  window.setTimeout(start, 2600); // backstop

  // 枠が可視になったら（まだなら）即読み込み。
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            start();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(shot);
  }
}

/**
 * 初期化。`[data-sp-devices]` が無ければ何もしない。
 *
 * @returns {void}
 */
export function initSiteDevices() {
  const root = document.querySelector("[data-sp-devices]");
  if (!root) {
    return;
  }
  root.querySelectorAll(".c-device-shot").forEach(initShot);
}
